import { OnvifVideoProfile } from "./onvifScanInterfaces";

export interface ProfileStream {
  /** NVR/encoder channel this profile belongs to, or null when undecidable. */
  channel: number | null;
  /** 0 = main stream, 1 = first sub-stream, etc. */
  streamIndex: number | null;
  /** Human label for the stream: "main", "sub1", … */
  role: string;
}

const roleForIndex = (streamIndex: number | null): string => {
  if (streamIndex == null) {
    return "";
  }
  return streamIndex === 0 ? "main" : `sub${streamIndex}`;
};

const firstNumber = (value: string | undefined): number | null => {
  if (value == null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Work out which camera channel and which stream of that channel an ONVIF profile
 * represents.
 *
 * A single host can front many cameras (an NVR channel per camera), and each
 * channel exposes several streams. Nothing in the ONVIF response says so
 * directly, so we read the device's own RTSP URI first and fall back to vendor
 * token conventions. Sources, most reliable first:
 *
 *  1. RTSP query args — `channel=N` with `subtype=`/`stream=` (Dahua family; this
 *     is what the "General NVR" at 172.16.237.59 returns), or LTS's
 *     `Ch=N&Streamtype=N`.
 *  2. RTSP path `/Streaming/Channels/{chID}` — Hikvision, where `chID` is
 *     `channel * 100 + streamIndex + 1` (101 = channel 1 main).
 *  3. Profile token `MediaProfile00{channel-1}{stream}` — Dahua tokens, e.g.
 *     `MediaProfile00100` is channel 2, stream 0.
 *  4. RTSP path `/media/videoN` — single-channel devices, stream `N-1`.
 *  5. Profile name ending in `MainStream` / `SubStreamN` — stream only.
 *  6. Nothing derivable — channel null, so callers keep one row per host.
 *
 * Stream index is deliberately preferred over reported resolution when picking a
 * primary stream: it comes from the vendor's own numbering and stays correct even
 * when a device misreports its encoder settings.
 */
export const deriveProfileStream = (
  profile: OnvifVideoProfile
): ProfileStream => {
  const uri = profile.rtsp_uri || "";
  const token = profile.profile_token || "";

  let channel: number | null = null;
  let streamIndex: number | null = null;

  // 1. Query args on the RTSP URI. `Ch=`/`Streamtype=` is LTS
  //    (`/video/livemedia?Ch=1&Streamtype=0`).
  const channelQuery = uri.match(/[?&](?:channel|ch)=(\d+)/i);
  if (channelQuery) {
    channel = firstNumber(channelQuery[1]);
  }
  const subtypeQuery = uri.match(/[?&](?:subtype|stream|streamtype)=(\d+)/i);
  if (subtypeQuery) {
    streamIndex = firstNumber(subtypeQuery[1]);
  }

  // 2. Hikvision channel id in the path.
  if (channel == null) {
    const hikvision = uri.match(/\/Streaming\/Channels\/(\d+)/i);
    if (hikvision) {
      const channelId = firstNumber(hikvision[1]);
      if (channelId != null) {
        // 101 -> channel 1 stream 0; a bare "1" means channel 1 with no stream id.
        channel = channelId >= 100 ? Math.floor(channelId / 100) : channelId;
        const trailing = channelId >= 100 ? channelId % 100 : 0;
        if (streamIndex == null && trailing > 0) {
          streamIndex = trailing - 1;
        }
      }
    }
  }

  // 3. Dahua-style profile token: three channel digits (0-based) + two stream digits.
  if (channel == null) {
    const dahuaToken = token.match(/(\d{3})(\d{2})$/);
    if (dahuaToken) {
      const channelIndex = firstNumber(dahuaToken[1]);
      if (channelIndex != null) {
        channel = channelIndex + 1;
      }
      if (streamIndex == null) {
        streamIndex = firstNumber(dahuaToken[2]);
      }
    }
  }

  // 4. /media/videoN — one channel, stream N-1.
  if (channel == null) {
    const mediaVideo = uri.match(/\/media\/video(\d+)/i);
    if (mediaVideo) {
      const videoNumber = firstNumber(mediaVideo[1]);
      if (videoNumber != null && videoNumber >= 1) {
        channel = 1;
        if (streamIndex == null) {
          streamIndex = videoNumber - 1;
        }
      }
    }
  }

  // 5. Profile name ending in "MainStream" / "SubStreamN" -- the office LTS
  //    names them "MediaProfile_Channel1_MainStream". Only the stream,
  //    never the channel: the name says nothing about which camera it is. This
  //    matters most when the device omits its encoder config (H.265 on Media1),
  //    since there is then no resolution to fall back on either.
  if (streamIndex == null) {
    const name = (profile.profile_name || "").replace(/[\s_-]/g, "");
    if (/mainstream$/i.test(name)) {
      streamIndex = 0;
    } else {
      const sub = name.match(/substream(\d*)$/i);
      if (sub) {
        streamIndex = sub[1] ? firstNumber(sub[1]) : 1;
      }
    }
  }

  return { channel, streamIndex, role: roleForIndex(streamIndex) };
};
